import React, { useState, useEffect, useRef } from 'react';
import { Settings, Save } from 'lucide-react';
import { Encounter } from '../../types/encounter';
import { EncounterSectionTemplate, QuestionType } from '../../types/encounterTemplate';
import { DischargeInstructionPreset } from '../../types/dischargeInstructionPreset';
import { getTemplatesForSection } from '../../utils/encounterTemplateApi';
import { getPresetsForPractice, createPreset } from '../../utils/dischargeInstructionPresetApi';
import { updateEncounter } from '../../utils/encounterApi';
import { useAuth } from '../../contexts/AuthContext';
import TemplateEditorModal from './TemplateEditorModal';

interface DischargeInstructionsSectionProps {
  encounter: Encounter | null;
  onUpdate: () => void;
}

const DischargeInstructionsSection: React.FC<DischargeInstructionsSectionProps> = ({ encounter, onUpdate }) => {
  const { user } = useAuth();
  const [templates, setTemplates] = useState<EncounterSectionTemplate[]>([]);
  const [presets, setPresets] = useState<DischargeInstructionPreset[]>([]);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(true);
  const [isLoadingPresets, setIsLoadingPresets] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [showSavePresetModal, setShowSavePresetModal] = useState(false);
  const [newPresetName, setNewPresetName] = useState('');
  const [selectedPresetId, setSelectedPresetId] = useState<string>('');
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Sync form data with encounter
  useEffect(() => {
    if (encounter) {
      setFormData(encounter.discharge_instructions_data || {});
    }
  }, [encounter]);

  // Fetch templates
  useEffect(() => {
    const fetchTemplates = async () => {
      if (!user?.practice_id) return;

      setIsLoadingTemplates(true);
      try {
        const data = await getTemplatesForSection(user.practice_id, 'discharge_instructions');
        setTemplates(data);
      } catch (error) {
        console.error('Failed to fetch templates:', error);
      } finally {
        setIsLoadingTemplates(false);
      }
    };

    fetchTemplates();
  }, [user?.practice_id]);

  // Fetch presets
  useEffect(() => {
    const fetchPresets = async () => {
      if (!user?.practice_id) return;

      setIsLoadingPresets(true);
      try {
        const data = await getPresetsForPractice(user.practice_id);
        setPresets(data);
      } catch (error) {
        console.error('Failed to fetch presets:', error);
      } finally {
        setIsLoadingPresets(false);
      }
    };

    fetchPresets();
  }, [user?.practice_id]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  const handleFieldChange = (questionKey: string, value: any) => {
    const updatedData = { ...formData, [questionKey]: value };
    setFormData(updatedData);

    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Set new autosave timeout (1 second debounce)
    if (encounter) {
      saveTimeoutRef.current = setTimeout(async () => {
        setIsSaving(true);
        try {
          await updateEncounter(encounter.pet_id, encounter.id, {
            discharge_instructions_data: updatedData,
          });
          onUpdate();
        } catch (error) {
          console.error('Failed to save discharge instructions:', error);
        }
        // Show "Saving..." for at least 800ms for visual feedback
        setTimeout(() => setIsSaving(false), 800);
      }, 1000);
    }
  };

  const handleTemplatesUpdate = async () => {
    if (!user?.practice_id) return;
    const data = await getTemplatesForSection(user.practice_id, 'discharge_instructions');
    setTemplates(data);
  };

  /**
   * Load data from a preset template
   * Extensible for future auto-population from encounter data
   */
  const loadDataFromPreset = (preset: DischargeInstructionPreset) => {
    setFormData(preset.preset_data);
    setSelectedPresetId(preset.id);
    
    // Trigger save after loading preset
    if (encounter) {
      updateEncounter(encounter.pet_id, encounter.id, {
        discharge_instructions_data: preset.preset_data,
      }).then(() => {
        onUpdate();
      }).catch((error) => {
        console.error('Failed to save preset data:', error);
      });
    }
  };

  /**
   * Future: Auto-populate discharge instructions from encounter data
   * 
   * This function is a placeholder for future functionality where discharge
   * instructions can be automatically populated from other sections of the encounter.
   * 
   * Example usage (to be implemented):
   * - procedures_performed: Pull from encounter.orders_data
   * - medications: Pull from prescription records linked to this encounter
   * - follow_up_appointments: Pull from scheduled future appointments for this pet
   * - diagnosis_summary: Pull from encounter.assessment_data
   * - emergency_contact: Pull from practice.emergency_contact or practice.phone
   * 
   * @param encounter - The current encounter object with all sections populated
   * @returns Record<string, string> - Populated discharge instruction fields
   * 
   * Implementation example:
   * const autoPopulateData = (encounter: Encounter): Record<string, string> => {
   *   return {
   *     procedures_performed: formatProcedures(encounter.orders_data),
   *     medications: formatMedications(encounter.prescriptions),
   *     diagnosis_summary: formatDiagnosis(encounter.assessment_data),
   *     // ... etc
   *   };
   * };
   */
  const loadDataFromEncounter = (encounter: Encounter): Record<string, string> => {
    // Placeholder - to be implemented when auto-population is needed
    console.log('Auto-population from encounter not yet implemented', encounter);
    return {};
  };

  const handleLoadPreset = () => {
    if (!selectedPresetId) return;
    
    const preset = presets.find(p => p.id === selectedPresetId);
    if (preset) {
      loadDataFromPreset(preset);
    }
  };

  const handleSaveAsPreset = async () => {
    if (!user?.practice_id || !newPresetName.trim()) return;

    try {
      await createPreset(user.practice_id, {
        preset_name: newPresetName,
        preset_data: formData,
      });
      
      // Refresh presets list
      const updatedPresets = await getPresetsForPractice(user.practice_id);
      setPresets(updatedPresets);
      
      // Close modal and reset
      setShowSavePresetModal(false);
      setNewPresetName('');
    } catch (error) {
      console.error('Failed to save preset:', error);
      alert('Failed to save preset. Please try again.');
    }
  };

  const renderField = (template: EncounterSectionTemplate) => {
    const value = formData[template.question_key] || '';

    switch (template.question_type) {
      case 'text':
        return (
          <textarea
            value={value}
            onChange={(e) => handleFieldChange(template.question_key, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            style={{ minHeight: '100px' }}
            placeholder={template.is_required ? 'Required' : 'Optional'}
          />
        );

      case 'yes_no':
        return (
          <div className="flex gap-4">
            <label className="flex items-center">
              <input
                type="radio"
                name={template.question_key}
                value="yes"
                checked={value === 'yes'}
                onChange={(e) => handleFieldChange(template.question_key, e.target.value)}
                className="mr-2"
              />
              Yes
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name={template.question_key}
                value="no"
                checked={value === 'no'}
                onChange={(e) => handleFieldChange(template.question_key, e.target.value)}
                className="mr-2"
              />
              No
            </label>
          </div>
        );

      case 'select':
        return (
          <select
            value={value}
            onChange={(e) => handleFieldChange(template.question_key, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Select...</option>
            {template.options?.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        );

      case 'multi_select':
        const selectedValues = Array.isArray(value) ? value : [];
        return (
          <div className="space-y-2">
            {template.options?.map((option) => (
              <label key={option} className="flex items-center">
                <input
                  type="checkbox"
                  checked={selectedValues.includes(option)}
                  onChange={(e) => {
                    const newValues = e.target.checked
                      ? [...selectedValues, option]
                      : selectedValues.filter((v: string) => v !== option);
                    handleFieldChange(template.question_key, newValues);
                  }}
                  className="mr-2"
                />
                {option}
              </label>
            ))}
          </div>
        );

      case 'date':
        return (
          <input
            type="date"
            value={value}
            onChange={(e) => handleFieldChange(template.question_key, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        );

      case 'number':
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => handleFieldChange(template.question_key, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder={template.is_required ? 'Required' : 'Optional'}
          />
        );

      default:
        return null;
    }
  };

  if (!encounter) {
    return (
      <div className="text-center py-8 text-gray-500">
        No encounter selected
      </div>
    );
  }

  if (isLoadingTemplates || isLoadingPresets) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Preset Selector */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h2 className="text-2xl font-bold text-gray-800">Discharge Instructions</h2>
        
        <div className="flex items-center gap-3 flex-wrap">
          {/* Preset Selector */}
          <div className="flex items-center gap-2">
            <select
              value={selectedPresetId}
              onChange={(e) => setSelectedPresetId(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select a preset...</option>
              {presets.map((preset) => (
                <option key={preset.id} value={preset.id}>
                  {preset.is_standard ? '⭐ ' : ''}{preset.preset_name}
                </option>
              ))}
            </select>
            <button
              onClick={handleLoadPreset}
              disabled={!selectedPresetId}
              className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              Load
            </button>
          </div>

          {/* Save as Preset Button */}
          <button
            onClick={() => setShowSavePresetModal(true)}
            className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            <Save className="w-4 h-4" />
            Save as Preset
          </button>

          {/* Edit Template Button */}
          <button
            onClick={() => setIsEditorOpen(true)}
            className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            <Settings className="w-4 h-4" />
            Edit Template
          </button>
        </div>
      </div>

      {/* Form Fields */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
        <div className="space-y-4">
          {templates
            .sort((a, b) => a.display_order - b.display_order)
            .map((template, index) => (
              <div
                key={template.id}
                className={`grid grid-cols-[240px_1fr] gap-4 items-start ${
                  index > 0 ? 'pt-4 border-t border-gray-200' : ''
                }`}
              >
                <label className="text-sm font-medium text-gray-700 pt-2">
                  {template.question_text}
                  {template.is_required && <span className="text-red-500 ml-1">*</span>}
                </label>
                <div>{renderField(template)}</div>
              </div>
            ))}
        </div>

        {templates.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            No template questions configured. Click "Edit Template" to add questions.
          </div>
        )}
      </div>

      {/* Saving Indicator */}
      {isSaving && (
        <div className="fixed bottom-4 right-4 bg-blue-600 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2">
          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
          Saving...
        </div>
      )}

      {/* Template Editor Modal */}
      {isEditorOpen && user?.practice_id && (
        <TemplateEditorModal
          practiceId={user.practice_id}
          sectionName="discharge_instructions"
          templates={templates}
          onClose={() => setIsEditorOpen(false)}
          onUpdate={() => {
            handleTemplatesUpdate();
            onUpdate();
          }}
        />
      )}

      {/* Save as Preset Modal */}
      {showSavePresetModal && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-50"
            onClick={() => setShowSavePresetModal(false)}
          />
          
          {/* Modal */}
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-2xl max-w-md w-full">
              {/* Header */}
              <div className="px-6 pt-6 pb-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  Save as Preset
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  Save the current discharge instructions as a reusable template
                </p>
              </div>

              {/* Content */}
              <div className="px-6 py-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Preset Name
                </label>
                <input
                  type="text"
                  value={newPresetName}
                  onChange={(e) => setNewPresetName(e.target.value)}
                  placeholder="e.g., TPLO Surgery, Dental Cleaning"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  autoFocus
                />
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end gap-3 px-6 py-4 bg-gray-50 rounded-b-lg">
                <button
                  onClick={() => {
                    setShowSavePresetModal(false);
                    setNewPresetName('');
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveAsPreset}
                  disabled={!newPresetName.trim()}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                >
                  Save Preset
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DischargeInstructionsSection;
